import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile } from '../../services/upload';
import { states, statesAndDistricts, getDistrictsByState } from '../../data/districts';
import './Dashboard.css';

const AgentDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    logoUrl: '',
    websiteUrl: '',
    latitude: '',
    longitude: '',
    district: '',
    state: '',
    brandGroupId: '',
    email: '',
    password: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [registering, setRegistering] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, shopsRes] = await Promise.all([
        api.get('/api/agent/profile'),
        api.get('/api/agent/shops')
      ]);
      setProfile(profileRes.data.data);
      setShops(shopsRes.data.data || []);
      
      // Auto-fill district and state for shop registration from profile
      if (profileRes.data.data && profileRes.data.data.district && profileRes.data.data.state) {
        setRegisterForm(prev => ({
          ...prev,
          district: profileRes.data.data.district,
          state: profileRes.data.data.state
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRegisterShop = async (e) => {
    e.preventDefault();
    setRegistering(true);
    setMessage({ type: '', text: '' });

    try {
      let finalFormData = { ...registerForm };

      // Upload logo if file is selected
      if (logoFile) {
        const uploadResult = await uploadFile(logoFile, 'logo');
        if (uploadResult.success) {
          finalFormData.logoUrl = uploadResult.url;
        } else {
          setMessage({ type: 'error', text: uploadResult.message || 'Error uploading logo' });
          setRegistering(false);
          return;
        }
      }

      // District is auto-filled from agent profile, don't send it - backend will use agent's district
      delete finalFormData.district;

      const response = await api.post('/api/agent/shops/register', finalFormData);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Shop registered successfully!' });
        setShowRegisterModal(false);
        // Reset form but keep district/state from profile
        const agentState = profile?.district ? (() => {
          for (const [state, districts] of Object.entries(statesAndDistricts)) {
            if (districts.includes(profile.district)) return state;
          }
          return '';
        })() : '';
        
        setRegisterForm({
          name: '',
          logoUrl: '',
          websiteUrl: '',
          latitude: '',
          longitude: '',
          district: profile?.district || '',
          state: agentState,
          brandGroupId: '',
          email: '',
          password: ''
        });
        setLogoFile(null);
        setLogoPreview('');
        fetchData();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error registering shop'
      });
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1 className="page-title">Agent Dashboard</h1>
            <p className="page-subtitle">Manage your registered shops</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowRegisterModal(true)}
          >
            + Register New Shop
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
            {message.text}
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card stat-card-primary">
            <div className="stat-icon">🏪</div>
            <div className="stat-content">
              <h3>Total Shops</h3>
              <div className="value">{shops.length}</div>
            </div>
          </div>
          <div className="stat-card stat-card-success">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Active Shops</h3>
              <div className="value">{shops.filter(s => s.isActive).length}</div>
            </div>
          </div>
          {profile && (
            <div className="stat-card stat-card-info">
              <div className="stat-icon">👤</div>
              <div className="stat-content">
                <h3>Agent Profile</h3>
                <div className="value-small">{profile.email}</div>
                {profile.name && <div className="value-small">{profile.name}</div>}
                {profile.district && (
                  <div className="value-small" style={{ marginTop: '5px', color: '#667eea', fontWeight: '600' }}>
                    📍 {profile.district}, {profile.state || ''}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Registered Shops</h2>
          </div>
          <div className="table-container">
            {shops.length === 0 ? (
              <div className="empty-state">
                <p>No shops registered yet. Click "Register New Shop" to get started.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Shop Name</th>
                    <th>District</th>
                    <th>State</th>
                    <th>Admin Email</th>
                    <th>Status</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((shop) => (
                    <tr key={shop._id}>
                      <td>
                        <div className="shop-info">
                          {shop.logoUrl && (
                            <img src={shop.logoUrl} alt={shop.name} className="shop-logo" />
                          )}
                          <span>{shop.name}</span>
                        </div>
                      </td>
                      <td>{shop.district}</td>
                      <td>{shop.state}</td>
                      <td>{shop.adminEmail || 'N/A'}</td>
                      <td>
                        <span className={`badge ${shop.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {shop.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{new Date(shop.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => {
          setShowRegisterModal(false);
          setLogoFile(null);
          setLogoPreview('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Register New Shop</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowRegisterModal(false);
                  setLogoFile(null);
                  setLogoPreview('');
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleRegisterShop} className="modal-form">
              {profile?.district && (
                <div className="form-group">
                  <label>District (Assigned)</label>
                  <input
                    type="text"
                    value={profile.district}
                    disabled
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    You can only register shops in your assigned district
                  </small>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Shop Name *</label>
                  <input
                    type="text"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <select
                    value={registerForm.state}
                    onChange={(e) => setRegisterForm({ ...registerForm, state: e.target.value })}
                    required
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Brand Group ID</label>
                  <input
                    type="text"
                    value={registerForm.brandGroupId}
                    onChange={(e) => setRegisterForm({ ...registerForm, brandGroupId: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={registerForm.latitude}
                    onChange={(e) => setRegisterForm({ ...registerForm, latitude: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={registerForm.longitude}
                    onChange={(e) => setRegisterForm({ ...registerForm, longitude: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Shop Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ marginBottom: '10px' }}
                />
                {logoPreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  value={registerForm.websiteUrl}
                  onChange={(e) => setRegisterForm({ ...registerForm, websiteUrl: e.target.value })}
                />
              </div>
              <div className="form-divider">
                <span>Admin Account</span>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Admin Email *</label>
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Admin Password *</label>
                  <input
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRegisterModal(false);
                    setLogoFile(null);
                    setLogoPreview('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={registering}>
                  {registering ? 'Registering...' : 'Register Shop'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDashboard;

